import type { ActionContext } from 'vuex';
import type { Filter } from '@/modules/shared/types/axios.type';
import { setStateLoadingStatusByType } from '@/modules/shared/utils/stateManagement';
import type { LoadingState } from '@/modules/shared/types/state.type';
import { getCaptionDraftList } from '@/modules/videoCaption/services';
import { notifyIfCaptionListChanged } from '@/modules/videoCaption/utils/notifyIfCaptionListChanged';
import type { CaptionDraft, CaptionListState } from '@/modules/videoCaption/types';
import { formatErrorObject } from '@/modules/shared/utils/errorFormatter';
import type { RootState } from '@/store/type';

type CaptionListContext = ActionContext<CaptionListState, RootState>;

// state
const initialState = (): CaptionListState => ({
  currentDuplicateTarget: null,
  captionList: null,
  lastPage: 1,
  total: 0,
  loadingState: setStateLoadingStatusByType(),
});

const state = initialState();

// mutation
const mutations = {
  setCaptionList(state: CaptionListState, captionList: CaptionDraft[]) {
    state.captionList = captionList;
  },
  updateCaptionByIndex(
    state: CaptionListState,
    { index, caption }: { index: number; caption: CaptionDraft },
  ) {
    if (state.captionList) {
      state.captionList[index] = caption;
    }
  },
  setCaptionListLoadingState(state: CaptionListState, loadingState: LoadingState) {
    state.loadingState = setStateLoadingStatusByType(loadingState);
  },
  setCaptionListLastPage(state: CaptionListState, lastPage: number) {
    state.lastPage = lastPage;
  },
  setTotalCaptionList(state: CaptionListState, total: number) {
    state.total = total;
  },
  setCurrentDuplicateTarget(state: CaptionListState, value: CaptionListState['currentDuplicateTarget']) {
    state.currentDuplicateTarget = value;
  },
  destroyCaptionList(state: CaptionListState) {
    Object.assign(state, initialState());
  },
};

// action
const actions = {
  async loadCaptionList({ commit }: CaptionListContext, payload: Filter & { silent?: boolean }) {
    const { videoKey, silent = false, ...filter } = payload;

    if (!silent) {
      commit('setCaptionListLoadingState', { type: 'loading' });
    }

    if (typeof videoKey !== 'string') {
      commit('setCaptionListLoadingState', {
        type: 'error',
        error: 'A project key is missing.',
      });
      return;
    }

    try {
      // caption drafts
      const response = await getCaptionDraftList(videoKey, filter);
      const { data, lastPage, total } = response.data;
      const captionDraftList = data;

      notifyIfCaptionListChanged(videoKey, captionDraftList);

      commit('setCaptionList', captionDraftList);
      commit('setCaptionListLastPage', lastPage);
      commit('setTotalCaptionList', total);
      commit('setCaptionListLoadingState', { type: 'success' });
    } catch (error) {
      commit('setCaptionListLoadingState', {
        type: 'error',
        error: formatErrorObject(error, 'Caption Draft List'),
      });
    }
  },

  async reloadCaptionList({ commit, dispatch }: CaptionListContext, payload: Filter & { silent?: boolean }) {
    const { videoKey } = payload;

    if (typeof videoKey !== 'string') {
      commit('setCaptionListLoadingState', {
        type: 'error',
        error: 'A project key is missing.',
      });
      return;
    }
    await dispatch('loadCaptionList', payload);
  },

  updateCaptionByIndex(
    { commit, state }: CaptionListContext,
    { index, payload }: { index: number; payload: Partial<CaptionDraft> },
  ) {
    if (state.captionList) {
      const caption = { ...state.captionList[index], ...payload };
      commit('updateCaptionByIndex', { index, caption });
    }
  },

  destroyCaptionList({ commit }: CaptionListContext) {
    commit('destroyCaptionList');
  },
};

export default {
  state,
  mutations,
  actions,
};
